import { useState } from 'react';
import { Cpu } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Device } from '@/api/types';

/**
 * Foto da máquina. Resolve a URL na mesma ordem que o app mobile:
 *
 * 1. `device.photo` (nome de arquivo) → `/photos/devices/<filename>`
 *    (precisa proxy /photos/ no nginx para o servidor Traccar)
 * 2. `device.attributes.image | photo | imageUrl | photoUrl | deviceImage |
 *    picture | foto | imagem` aceitando URL absoluta, caminho relativo ou
 *    nome de arquivo.
 *
 * Quando nada bate ou a imagem falha em carregar, exibe placeholder com
 * gradiente azul + ícone.
 */

interface Props {
  device: Device;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const SIZES: Record<NonNullable<Props['size']>, string> = {
  sm: 'h-12 w-12',
  md: 'h-16 w-16',
  lg: 'h-24 w-24',
  xl: 'h-32 w-32',
};

const ICON_SIZES: Record<NonNullable<Props['size']>, string> = {
  sm: 'h-5 w-5',
  md: 'h-7 w-7',
  lg: 'h-10 w-10',
  xl: 'h-14 w-14',
};

const ATTR_KEYS = [
  'deviceImage',
  'image',
  'photo',
  'picture',
  'foto',
  'imagem',
  'imageUrl',
  'photoUrl',
];

function buildPhotoPath(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) return trimmed;
  // nome de arquivo só: convenção do Traccar
  return `/photos/devices/${encodeURIComponent(trimmed)}`;
}

function resolvePhotoUrl(device: Device): string | undefined {
  if (device.photo && device.photo.trim()) {
    return buildPhotoPath(device.photo);
  }
  const a = device.attributes;
  if (!a) return undefined;
  for (const key of ATTR_KEYS) {
    const v = a[key];
    if (typeof v === 'string' && v.trim()) {
      return buildPhotoPath(v);
    }
  }
  return undefined;
}

export function DevicePhoto({ device, size = 'md', className }: Props) {
  const [failed, setFailed] = useState(false);
  const url = resolvePhotoUrl(device);

  const wrapper = cn(
    'relative shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-primary/10 to-primary/5',
    'flex items-center justify-center',
    SIZES[size],
    className,
  );

  if (url && !failed) {
    return (
      <div className={wrapper}>
        <img
          src={url}
          alt={device.name}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }

  return (
    <div className={wrapper}>
      <Cpu className={cn(ICON_SIZES[size], 'text-primary/60')} />
    </div>
  );
}
